import {
  areasForCascade,
  changeCascade,
  equipmentsForCascade,
  LatestRequestGate,
  projectsForCascade,
  type CascadeLevel,
  type CascadeSelection
} from "../context/client-cascade.js";

export interface CascadeClientOption { id: string; name: string }
export interface CascadeProjectOption { id: string; code: string; name: string; clientId: string }
export interface CascadeAreaOption { id: string; code: string; name: string; clientId: string; projectId: string }
export interface CascadeEquipmentOption {
  id: string;
  tag: string;
  name: string;
  clientId: string;
  projectId?: string | null;
  unitAreaId?: string | null;
  areaId?: string | null;
}

export interface AssetCascadeSources {
  readonly clients: readonly CascadeClientOption[];
  readonly projects: readonly CascadeProjectOption[];
  readonly areas: readonly CascadeAreaOption[];
  readonly authorizedClientIds: ReadonlySet<string>;
  /** Equipamentos do Cliente (a página guarda em cache por Cliente). */
  equipmentsOf(clientId: string): Promise<readonly CascadeEquipmentOption[]>;
}

export interface AssetCascadeOptions {
  /** Edição: o Cliente do registro é fixo e só aparece, sem poder trocar. */
  readonly lockClient: boolean;
  readonly equipmentLabel: string;
}

const esc = (value: unknown): string =>
  String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const FIELD_STYLE = "width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);";
const LABEL_STYLE = "display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;";

/**
 * CLIENTE * → PROJETO → UNIDADE / ÁREA → EQUIPAMENTO dos formulários de
 * Instrumento e PSV.
 *
 * Trocar um nível limpa os de baixo (`changeCascade`). Os equipamentos são
 * carregados por Cliente; se o usuário troca A → B antes de A responder, a
 * resposta de A é descartada (`LatestRequestGate`) — não desenha por cima de B.
 */
export class AssetCascadeFields {
  private selection: CascadeSelection;
  private equipments: readonly CascadeEquipmentOption[] = [];
  private equipmentsLoading = false;
  private readonly gate = new LatestRequestGate();

  constructor(
    private readonly host: HTMLElement,
    private readonly sources: AssetCascadeSources,
    initial: CascadeSelection,
    private readonly options: AssetCascadeOptions
  ) {
    this.selection = initial;
  }

  get value(): CascadeSelection {
    return this.selection;
  }

  async mount(): Promise<void> {
    this.render();
    await this.loadEquipments();
  }

  private async loadEquipments(): Promise<void> {
    const ticket = this.gate.begin();
    const clientId = this.selection.clientId;
    if (!clientId) {
      this.equipments = [];
      this.equipmentsLoading = false;
      this.render();
      return;
    }
    this.equipmentsLoading = true;
    this.render();
    const list = await this.sources.equipmentsOf(clientId);
    if (!this.gate.isCurrent(ticket)) return;
    this.equipments = list;
    this.equipmentsLoading = false;
    this.render();
  }

  /** Mantém visível um valor que não está entre as opções (cadastro antigo), para a edição não apagá-lo. */
  private keepCurrent(value: string, ids: readonly string[], label: string): string {
    return value && !ids.includes(value) ? `<option value="${esc(value)}" selected>${esc(label)}</option>` : "";
  }

  private render(): void {
    const sel = this.selection;
    const projects = projectsForCascade(this.sources.projects, sel, this.sources.authorizedClientIds).filter(p => !!sel.clientId);
    const areas = sel.clientId ? areasForCascade(this.sources.areas, sel, this.sources.authorizedClientIds) : [];
    const equipments = equipmentsForCascade(this.equipments, sel);
    const clientName = this.sources.clients.find(c => c.id === sel.clientId)?.name ?? sel.clientId;

    const clientField = this.options.lockClient
      ? `<select data-cascade="clientId" disabled style="${FIELD_STYLE} opacity: 0.75;" title="O Cliente de um cadastro existente não é alterado."><option value="${esc(sel.clientId)}" selected>${esc(clientName)}</option></select>`
      : `<select data-cascade="clientId" required style="${FIELD_STYLE}">
           <option value="">Selecione o Cliente…</option>
           ${this.sources.clients.map(c => `<option value="${esc(c.id)}" ${sel.clientId === c.id ? "selected" : ""}>${esc(c.name)}</option>`).join("")}
         </select>`;

    this.host.innerHTML = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
        <div>
          <label style="${LABEL_STYLE}">Cliente *</label>
          ${clientField}
        </div>
        <div>
          <label style="${LABEL_STYLE}">Projeto</label>
          <select data-cascade="projectId" style="${FIELD_STYLE}" ${sel.clientId ? "" : "disabled"}>
            <option value="">${sel.clientId ? "Nenhum Projeto específico" : "Selecione o Cliente primeiro"}</option>
            ${projects.map(p => `<option value="${esc(p.id)}" ${sel.projectId === p.id ? "selected" : ""}>${esc(p.code)} — ${esc(p.name)}</option>`).join("")}
            ${this.keepCurrent(sel.projectId, projects.map(p => p.id), "Projeto atual do cadastro")}
          </select>
        </div>
        <div>
          <label style="${LABEL_STYLE}">Unidade / Área</label>
          <select data-cascade="areaId" style="${FIELD_STYLE}" ${sel.clientId ? "" : "disabled"}>
            <option value="">${sel.clientId ? "Nenhuma Unidade / Área específica" : "Selecione o Cliente primeiro"}</option>
            ${areas.map(a => `<option value="${esc(a.id)}" ${sel.areaId === a.id ? "selected" : ""}>${esc([a.code, a.name].filter(Boolean).join(" - "))}</option>`).join("")}
            ${this.keepCurrent(sel.areaId, areas.map(a => a.id), "Área anterior do cadastro (legado)")}
          </select>
        </div>
        <div>
          <label style="${LABEL_STYLE}">${esc(this.options.equipmentLabel)}</label>
          <select data-cascade="equipmentId" style="${FIELD_STYLE}" ${sel.clientId && !this.equipmentsLoading ? "" : "disabled"}>
            <option value="">${this.equipmentsLoading ? "Carregando equipamentos…" : "Não vinculado a equipamento específico"}</option>
            ${equipments.map(e => `<option value="${esc(e.id)}" ${sel.equipmentId === e.id ? "selected" : ""}>${esc(e.tag)} - ${esc(e.name)}</option>`).join("")}
            ${this.equipmentsLoading ? "" : this.keepCurrent(sel.equipmentId, equipments.map(e => e.id), "Equipamento atual do cadastro")}
          </select>
        </div>
      </div>`;

    this.host.querySelectorAll<HTMLSelectElement>("select[data-cascade]").forEach(select => {
      select.addEventListener("change", () => {
        const level = select.dataset["cascade"] as CascadeLevel;
        this.selection = changeCascade(this.selection, level, select.value);
        if (level === "clientId") void this.loadEquipments();
        else this.render();
      });
    });
  }
}
